import { BigNumber } from '@ethersproject/bignumber'
import { useMemo } from 'react'

import { HolderOnChainData, PairOnChainData } from './usePairOnChainData'
import { TokenPrices } from './useTokenPrices'

export interface PairUSDValue {
  pairAddress: string
  chainId: number
  tvlUsd: number
  lpPriceUsd: number
  reserve0Usd: number
  reserve1Usd: number
  token0Symbol: string
  token1Symbol: string
}

export interface HolderUSDValue {
  holderAddress: string
  pairAddress: string
  chainId: number
  balance: string // formatted balance
  balanceRaw: BigNumber
  balanceUsd: number
  sharePercent: number
}

// Convert BigNumber to number with decimals
function formatUnits(value: BigNumber, decimals: number): number {
  const divisor = BigNumber.from(10).pow(decimals)
  const beforeDecimal = value.div(divisor)
  const afterDecimal = value.mod(divisor)

  // Handle precision carefully
  const scaledAfter = afterDecimal.mul(1e9).div(divisor)
  return beforeDecimal.toNumber() + scaledAfter.toNumber() / 1e9
}

export function calculatePairUSDValues(
  pairData: { [pairAddress: string]: PairOnChainData },
  prices: TokenPrices
): { [pairAddress: string]: PairUSDValue } {
  const result: { [pairAddress: string]: PairUSDValue } = {}

  for (const [pairAddress, pair] of Object.entries(pairData)) {
    const price0 = prices[pair.token0Address] || 0
    const price1 = prices[pair.token1Address] || 0

    const reserve0Value = formatUnits(pair.reserve0, pair.token0Decimals)
    const reserve1Value = formatUnits(pair.reserve1, pair.token1Decimals)

    const reserve0Usd = reserve0Value * price0
    const reserve1Usd = reserve1Value * price1
    const tvlUsd = reserve0Usd + reserve1Usd

    const totalSupplyValue = formatUnits(pair.totalSupply, 18) // LP tokens always 18 decimals
    const lpPriceUsd = totalSupplyValue > 0 ? tvlUsd / totalSupplyValue : 0

    result[pairAddress] = {
      pairAddress,
      chainId: pair.chainId,
      tvlUsd,
      lpPriceUsd,
      reserve0Usd,
      reserve1Usd,
      token0Symbol: pair.token0Symbol,
      token1Symbol: pair.token1Symbol
    }
  }

  return result
}

export function calculateHolderUSDValues(
  holderData: HolderOnChainData[],
  pairData: { [pairAddress: string]: PairOnChainData },
  pairValues: { [pairAddress: string]: PairUSDValue }
): HolderUSDValue[] {
  return holderData.map(holder => {
    const pair = pairData[holder.pairAddress]
    const pairValue = pairValues[holder.pairAddress]

    const balanceFormatted = formatUnits(holder.balance, 18)
    const totalSupply = pair ? formatUnits(pair.totalSupply, 18) : 0

    const sharePercent = totalSupply > 0 ? (balanceFormatted / totalSupply) * 100 : 0
    const balanceUsd = pairValue ? balanceFormatted * pairValue.lpPriceUsd : 0

    return {
      holderAddress: holder.holderAddress,
      pairAddress: holder.pairAddress,
      chainId: holder.chainId,
      balance: balanceFormatted.toFixed(6),
      balanceRaw: holder.balance,
      balanceUsd,
      sharePercent
    }
  })
}

export function usePairUSDValues(
  pairData: { [pairAddress: string]: PairOnChainData },
  prices: TokenPrices
): { [pairAddress: string]: PairUSDValue } {
  return useMemo(() => {
    return calculatePairUSDValues(pairData, prices)
  }, [pairData, prices])
}

export function useHolderUSDValues(
  holderData: HolderOnChainData[],
  pairData: { [pairAddress: string]: PairOnChainData },
  pairValues: { [pairAddress: string]: PairUSDValue }
): HolderUSDValue[] {
  return useMemo(() => {
    return calculateHolderUSDValues(holderData, pairData, pairValues)
  }, [holderData, pairData, pairValues])
}

// Aggregate statistics
export function useTotalStats(pairValues: { [pairAddress: string]: PairUSDValue }) {
  return useMemo(() => {
    const pairs = Object.values(pairValues)
    const totalTvl = pairs.reduce((sum, p) => sum + p.tvlUsd, 0)
    const pairCount = pairs.length

    return {
      totalTvl,
      pairCount
    }
  }, [pairValues])
}
